export type CaptionAnimation =
  | 'pop'
  | 'fade'
  | 'slide'
  | 'scale'
  | 'glitch'
  | 'wiggle'
  | 'stretch'
  | 'pulse'
  | 'bounce'
  | 'blur-in'
  | 'flip-up'
  | 'drift'

export type CaptionHighlightMode = 'fill' | 'box' | 'underline' | 'glow'
export type CaptionBoxScope = 'word' | 'line'
export type CaptionIdleAnimation = 'none' | 'drift-x' | 'float' | 'wiggle' | 'pulse'
export type CaptionBackgroundScope = 'cue' | 'line'

export interface CaptionCssConfig {
  fontFamily?: string
  fontWeight?: number
  fontSize?: number
  color?: string
  WebkitTextStrokeColor?: string
  WebkitTextStrokeWidth?: number
  textShadow?: string
  textTransform?: 'none' | 'uppercase'
  letterSpacing?: number
  lineHeight?: number
  textAlign?: 'left' | 'center' | 'right'
  fontStyle?: 'normal' | 'italic'
  opacity?: number
}

export interface CaptionFontLayer {
  css: CaptionCssConfig
}

export interface CaptionHighlightLayer {
  mode: CaptionHighlightMode
  scope: CaptionBoxScope
  lineIndex: number
  css: {
    color?: string
    backgroundColor?: string
    underlineColor?: string
    underlineHeight?: number
    borderRadius?: number
    padding?: string
  }
}

export interface CaptionBackgroundLayer {
  scope: CaptionBackgroundScope
  css: {
    backgroundColor?: string
    color?: string
    borderRadius?: number
    boxShadow?: string
    padding?: string
  }
}

export interface CaptionAnimationLayer {
  word: CaptionAnimation
  idle: CaptionIdleAnimation
  wordDurationMs: number
  wordEasing: string
  wordDistanceEm: number
  wordScaleFrom: number
  idleDurationMs: number
  idleOffsetPx: number
  staggerMs: number
}

export interface CaptionOverlayConfig {
  top?: number
  left?: number
  width?: number
  height?: number
  rotation?: number
}

export interface CaptionStyleConfig {
  font: CaptionFontLayer
  highlightFont: CaptionHighlightLayer | null
  sleeperFont: CaptionFontLayer | null
  background: CaptionBackgroundLayer | null
  animation: CaptionAnimationLayer | null
  maxWords: number
  wordGap: number
  lineOffsetY?: number
  overlay?: CaptionOverlayConfig
}

export interface CaptionCueWord {
  word: string
  startMs: number
  endMs: number
}

export interface CaptionCue {
  text: string
  startMs: number
  endMs: number
  words: CaptionCueWord[]
}

export interface CaptionRenderConfig {
  fontFamily: string
  fontSize: number
  color: string
  fontWeight: number
  strokeColor: string
  strokeWidth: number
  textTransform: 'none' | 'uppercase'
  letterSpacing: number
  lineHeight: number
  textAlign: 'left' | 'center' | 'right'
  textShadow: string
  wordGap: number
  maxWordsPerLine: number
  staggerMs: number
  italic: boolean
  wordAnimation: CaptionAnimation
  idleAnimation: CaptionIdleAnimation
  wordDurationMs: number
  wordEasing: string
  wordDistanceEm: number
  wordScaleFrom: number
  idleDurationMs: number
  idleOffsetPx: number
  highlightMode: CaptionHighlightMode
  boxScope: CaptionBoxScope
  highlightLineIndex: number
  highlightColor?: string
  highlightTextColor?: string
  highlightBoxColor?: string
  highlightUnderlineColor?: string
  highlightUnderlineHeight: number
  borderRadius: number
  paddingX: number
  paddingY: number
  sleeperColor?: string
  sleeperOpacity?: number
  backgroundColor?: string
  backgroundScope?: CaptionBackgroundScope
  backgroundTextColor?: string
  backgroundRadius: number
  backgroundPaddingX: number
  backgroundPaddingY: number
  backgroundShadow?: string
  lineOffsetY: number
}

type CaptionStyleSeed = {
  fontFamily?: string
  color?: string
  strokeColor?: string
  strokeWidth?: number
  fontSize?: number
  fontWeight?: number
  textTransform?: 'none' | 'uppercase'
  letterSpacing?: number
  lineHeight?: number
  textAlign?: 'left' | 'center' | 'right'
  textShadow?: string
  italic?: boolean
  highlightColor?: string
  highlightMode?: CaptionHighlightMode
  boxScope?: CaptionBoxScope
  highlightLineIndex?: number
  highlightTextColor?: string
  highlightBoxColor?: string
  highlightUnderlineColor?: string
  highlightUnderlineHeight?: number
  borderRadius?: number
  paddingX?: number
  paddingY?: number
  backgroundColor?: string
  backgroundTextColor?: string
  backgroundScope?: CaptionBackgroundScope
  backgroundRadius?: number
  backgroundPaddingX?: number
  backgroundPaddingY?: number
  backgroundShadow?: string
  sleeperColor?: string
  sleeperOpacity?: number
  wordGap?: number
  maxWords?: number
  wordAnimation?: CaptionAnimation
  wordDurationMs?: number
  wordEasing?: string
  wordDistanceEm?: number
  wordScaleFrom?: number
  idleAnimation?: CaptionIdleAnimation
  idleDurationMs?: number
  idleOffsetPx?: number
  staggerMs?: number
  lineOffsetY?: number
}

function toPadding(paddingY: number, paddingX: number) {
  return `${paddingY}px ${paddingX}px`
}

function parsePadding(padding: string | undefined, fallbackX: number, fallbackY: number) {
  if (!padding) return { x: fallbackX, y: fallbackY }
  const parts = padding.split(/\s+/).map((part) => Number.parseInt(part.replace('px', ''), 10)).filter((value) => Number.isFinite(value))
  if (parts.length === 1) return { x: parts[0]!, y: parts[0]! }
  if (parts.length >= 2) return { x: parts[1]!, y: parts[0]! }
  return { x: fallbackX, y: fallbackY }
}

const BASE_SEED: Required<CaptionStyleSeed> = {
  fontFamily: 'Inter',
  color: '#FFFFFF',
  strokeColor: '#000000',
  strokeWidth: 3,
  fontSize: 46,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: -0.03,
  lineHeight: 1.08,
  textAlign: 'center',
  textShadow: '0 2px 10px rgba(0,0,0,0.45)',
  italic: false,
  highlightColor: '#A3E635',
  highlightMode: 'fill',
  boxScope: 'word',
  highlightLineIndex: 0,
  highlightTextColor: '#FFFFFF',
  highlightBoxColor: '#6B46FF',
  highlightUnderlineColor: '#A3E635',
  highlightUnderlineHeight: 6,
  borderRadius: 12,
  paddingX: 10,
  paddingY: 6,
  backgroundColor: '',
  backgroundTextColor: '#FFFFFF',
  backgroundScope: 'cue',
  backgroundRadius: 12,
  backgroundPaddingX: 16,
  backgroundPaddingY: 6,
  backgroundShadow: '0 6px 16px rgba(0,0,0,0.18)',
  sleeperColor: '#D4D4D4',
  sleeperOpacity: 0.78,
  wordGap: 10,
  maxWords: 3,
  wordAnimation: 'pop',
  wordDurationMs: 480,
  wordEasing: 'cubic-bezier(0.22, 1, 0.36, 1)',
  wordDistanceEm: 0.62,
  wordScaleFrom: 0.42,
  idleAnimation: 'none',
  idleDurationMs: 2200,
  idleOffsetPx: 8,
  staggerMs: 90,
  lineOffsetY: -8,
}

function createStyle(seed: CaptionStyleSeed = {}): CaptionStyleConfig {
  const merged = { ...BASE_SEED, ...seed }
  return {
    font: {
      css: {
        fontFamily: merged.fontFamily,
        fontWeight: merged.fontWeight,
        fontSize: merged.fontSize,
        color: merged.color,
        WebkitTextStrokeColor: merged.strokeColor,
        WebkitTextStrokeWidth: merged.strokeWidth,
        textShadow: merged.textShadow,
        textTransform: merged.textTransform,
        letterSpacing: merged.letterSpacing,
        lineHeight: merged.lineHeight,
        textAlign: merged.textAlign,
        fontStyle: merged.italic ? 'italic' : 'normal',
      },
    },
    highlightFont: {
      mode: merged.highlightMode,
      scope: merged.boxScope,
      lineIndex: merged.highlightLineIndex,
      css: {
        color: merged.highlightColor,
        backgroundColor: merged.boxScope === 'word' && merged.highlightMode === 'box' ? merged.highlightBoxColor : undefined,
        underlineColor: merged.highlightUnderlineColor,
        underlineHeight: merged.highlightUnderlineHeight,
        borderRadius: merged.borderRadius,
        padding: toPadding(merged.paddingY, merged.paddingX),
      },
    },
    sleeperFont: {
      css: {
        color: merged.sleeperColor,
        opacity: merged.sleeperOpacity,
      },
    },
    background: merged.backgroundColor || (merged.boxScope === 'line' && merged.highlightMode === 'box')
        ? {
          scope: merged.backgroundScope,
          css: {
            backgroundColor: merged.backgroundColor || merged.highlightBoxColor,
            color: merged.backgroundTextColor,
            borderRadius: merged.backgroundRadius || merged.borderRadius,
            boxShadow: merged.backgroundShadow,
            padding: toPadding(merged.backgroundPaddingY, merged.backgroundPaddingX),
          },
        }
      : null,
    animation: {
      word: merged.wordAnimation,
      idle: merged.idleAnimation,
      wordDurationMs: merged.wordDurationMs,
      wordEasing: merged.wordEasing,
      wordDistanceEm: merged.wordDistanceEm,
      wordScaleFrom: merged.wordScaleFrom,
      idleDurationMs: merged.idleDurationMs,
      idleOffsetPx: merged.idleOffsetPx,
      staggerMs: merged.staggerMs,
    },
    maxWords: merged.maxWords,
    wordGap: merged.wordGap,
    lineOffsetY: merged.lineOffsetY,
  }
}

const WHITE = createStyle({})
export const captionStyleConfigMap: Record<string, CaptionStyleConfig> = {
  blue: createStyle({
    color: '#FFFFFF',
    strokeColor: '#0B1320',
    strokeWidth: 3,
    highlightColor: '#39C3FF',
    textShadow: '0 3px 12px rgba(0,0,0,0.5)',
    wordAnimation: 'slide',
    wordDurationMs: 420,
    wordDistanceEm: 0.52,
  }),
  deepdiver: createStyle({
    color: '#CFC7C5',
    strokeWidth: 0,
    textTransform: 'none',
    fontWeight: 700,
    textShadow: 'none',
    wordAnimation: 'fade',
    wordDurationMs: 360,
    wordEasing: 'ease-out',
    lineHeight: 1.15,
    highlightMode: 'box',
    boxScope: 'line',
    highlightColor: '#807B79',
    backgroundColor: '#F2EEF3',
    backgroundTextColor: '#8E8A8A',
    backgroundPaddingX: 17,
    backgroundPaddingY: 2,
    backgroundRadius: 11,
    backgroundShadow: '0 2px 8px rgba(0,0,0,0.08)',
    sleeperColor: '#DFD9DE',
    idleAnimation: 'float',
    idleDurationMs: 2800,
    idleOffsetPx: 4,
    lineOffsetY: -9,
  }),
  popline: createStyle({
    color: '#F5F3FF',
    strokeColor: '#1A1A1A',
    strokeWidth: 0,
    textTransform: 'none',
    fontSize: 42,
    fontWeight: 500,
    wordAnimation: 'fade',
    wordDurationMs: 320,
    wordEasing: 'ease-out',
    highlightMode: 'box',
    boxScope: 'word',
    highlightColor: '#F5F3FF',
    highlightTextColor: '#F5F3FF',
    highlightBoxColor: '#6D49D1',
    paddingX: 3,
    paddingY: 1,
    sleeperColor: '#E6DEF9',
    sleeperOpacity: 0.9,
  }),
  beasty: createStyle({
    color: '#FFFFFF',
    strokeColor: '#111111',
    strokeWidth: 5,
    fontWeight: 900,
    italic: true,
    highlightColor: '#FFE600',
    wordAnimation: 'scale',
    wordDurationMs: 380,
    wordScaleFrom: 0.58,
    textShadow: '0 4px 0 rgba(0,0,0,0.32)',
  }),
  phantom: createStyle({
    color: '#F5F5F5',
    strokeWidth: 0,
    fontWeight: 800,
    highlightColor: '#F5F5F5',
    sleeperColor: '#D9D9D9',
    sleeperOpacity: 0.58,
    textShadow: '0 0 14px rgba(255,255,255,0.35)',
    wordAnimation: 'blur-in',
    wordDurationMs: 420,
  }),
  playdate: createStyle({
    fontFamily: 'Georgia',
    color: '#665E56',
    strokeWidth: 0,
    textTransform: 'none',
    fontWeight: 700,
    highlightColor: '#665E56',
    sleeperColor: '#7E766E',
    sleeperOpacity: 0.72,
    wordAnimation: 'fade',
    lineHeight: 1.14,
  }),
  galaxy: createStyle({
    color: '#C9B6FF',
    strokeWidth: 0,
    fontSize: 40,
    highlightMode: 'box',
    boxScope: 'word',
    highlightTextColor: '#EFEAFF',
    highlightBoxColor: '#2A1A70',
    paddingX: 7,
    paddingY: 4,
    sleeperColor: '#A28DD5',
    sleeperOpacity: 0.68,
    wordAnimation: 'pulse',
    idleAnimation: 'drift-x',
    wordDurationMs: 520,
    idleDurationMs: 1900,
    idleOffsetPx: 12,
    staggerMs: 70,
  }),
  turban: createStyle({
    color: '#C6F5FF',
    strokeWidth: 0,
    fontSize: 42,
    highlightMode: 'box',
    boxScope: 'line',
    backgroundColor: '#7344D1',
    backgroundTextColor: '#C6F5FF',
    backgroundPaddingX: 18,
    backgroundPaddingY: 2,
    backgroundRadius: 4,
    wordAnimation: 'bounce',
    wordDurationMs: 430,
    wordScaleFrom: 0.72,
    idleAnimation: 'drift-x',
    idleDurationMs: 2100,
    idleOffsetPx: 10,
    lineOffsetY: -9,
  }),
  flipper: createStyle({
    color: '#111111',
    strokeWidth: 0,
    fontSize: 40,
    highlightMode: 'box',
    boxScope: 'line',
    backgroundColor: '#F7DB4C',
    backgroundTextColor: '#111111',
    wordAnimation: 'flip-up',
    wordDurationMs: 400,
    backgroundRadius: 2,
    backgroundPaddingY: 2,
    lineOffsetY: -8,
  }),
  spell: createStyle({
    color: '#BBA9E6',
    strokeWidth: 0,
    highlightMode: 'box',
    boxScope: 'line',
    backgroundColor: '#7650C6',
    backgroundTextColor: '#CDBDF0',
    wordAnimation: 'drift',
    wordDurationMs: 440,
    wordDistanceEm: 0.3,
    idleAnimation: 'drift-x',
    idleDurationMs: 1700,
    idleOffsetPx: 11,
    lineOffsetY: -9,
  }),
  youshaei: createStyle({
    color: '#7CF2C3',
    strokeWidth: 0,
    highlightColor: '#7CF2C3',
    wordAnimation: 'fade',
  }),
  podp: createStyle({
    color: '#FF4FE1',
    strokeColor: '#0E0E0E',
    strokeWidth: 4,
    fontSize: 54,
    fontWeight: 900,
    highlightColor: '#FF4FE1',
    sleeperColor: '#B99AB6',
    sleeperOpacity: 0.4,
    textShadow: '0 3px 10px rgba(0,0,0,0.24)',
    wordAnimation: 'bounce',
  }),
  noah: createStyle({
    color: '#FFFFFF',
    strokeColor: '#1A1A1A',
    strokeWidth: 3,
    italic: true,
    highlightColor: '#FFFFFF',
    sleeperColor: '#E2E2E2',
    sleeperOpacity: 0.56,
    wordAnimation: 'slide',
  }),
  drive: createStyle({
    color: '#4791FF',
    strokeWidth: 0,
    textTransform: 'none',
    highlightMode: 'box',
    boxScope: 'line',
    backgroundColor: '#0B0B0F',
    backgroundTextColor: '#4791FF',
    wordAnimation: 'slide',
  }),
  orange: createStyle({
    fontFamily: 'Georgia',
    color: '#7E4C2D',
    strokeWidth: 0,
    textTransform: 'none',
    highlightColor: '#7E4C2D',
    sleeperColor: '#94725C',
    sleeperOpacity: 0.68,
    wordAnimation: 'fade',
  }),
  ghost: createStyle({
    color: '#FFFFFF',
    strokeWidth: 0,
    highlightColor: '#FFFFFF',
    sleeperColor: '#ECECEC',
    sleeperOpacity: 0.54,
    textShadow: '0 0 12px rgba(255,255,255,0.8)',
    wordAnimation: 'blur-in',
  }),
  probox: createStyle({
    color: '#F4EDE1',
    strokeColor: '#6B5F46',
    strokeWidth: 1,
    textTransform: 'none',
    fontSize: 34,
    paddingX: 6,
    paddingY: 3,
    highlightMode: 'box',
    boxScope: 'line',
    backgroundColor: '#74674F',
    backgroundTextColor: '#F4EDE1',
    backgroundPaddingX: 12,
    backgroundPaddingY: 2,
    wordAnimation: 'fade',
  }),
  webster: createStyle({
    fontFamily: 'Playfair Display',
    color: '#F4F4F4',
    strokeWidth: 0,
    textTransform: 'none',
    fontSize: 34,
    fontWeight: 600,
    italic: true,
    letterSpacing: -0.018,
    lineHeight: 1.08,
    highlightColor: '#F4F4F4',
    sleeperColor: '#D0D0D0',
    sleeperOpacity: 0.5,
    textShadow: '0 2px 10px rgba(0,0,0,0.28)',
    wordAnimation: 'fade',
    wordDurationMs: 340,
    wordEasing: 'ease-out',
    idleAnimation: 'float',
    idleDurationMs: 2600,
    idleOffsetPx: 4,
    maxWords: 4,
    lineOffsetY: -3,
  }),
  lumina: createStyle({
    fontFamily: 'Playfair Display',
    color: '#E9E7EC',
    strokeWidth: 0,
    textTransform: 'none',
    fontSize: 40,
    fontWeight: 600,
    italic: true,
    letterSpacing: -0.018,
    lineHeight: 1.02,
    highlightMode: 'fill',
    highlightColor: '#FFFFFF',
    sleeperColor: '#9B97A0',
    sleeperOpacity: 0.78,
    textShadow: '0 2px 10px rgba(0,0,0,0.24)',
    wordAnimation: 'fade',
    wordDurationMs: 320,
    wordEasing: 'ease-out',
    idleAnimation: 'drift-x',
    idleDurationMs: 2200,
    idleOffsetPx: 6,
    lineOffsetY: -2,
    maxWords: 4,
  }),
  indigo: createStyle({
    color: '#E9E4FF',
    strokeWidth: 0,
    textTransform: 'none',
    highlightMode: 'box',
    boxScope: 'line',
    backgroundColor: '#2C2168',
    backgroundTextColor: '#F3F1FF',
    wordAnimation: 'fade',
    wordDurationMs: 330,
    idleAnimation: 'drift-x',
    idleDurationMs: 1600,
    idleOffsetPx: 10,
    lineOffsetY: -9,
  }),
  ember: createStyle({
    color: '#FFAA00',
    strokeWidth: 0,
    textTransform: 'none',
    fontSize: 44,
    highlightColor: '#FFAA00',
    sleeperColor: '#D09245',
    sleeperOpacity: 0.6,
    wordAnimation: 'scale',
  }),
  glow: createStyle({
    color: '#0C0C0C',
    strokeWidth: 0,
    textTransform: 'uppercase',
    fontSize: 48,
    fontWeight: 900,
    highlightMode: 'box',
    boxScope: 'word',
    highlightColor: '#111111',
    highlightTextColor: '#111111',
    highlightBoxColor: '#F0FF1D',
    borderRadius: 16,
    paddingX: 10,
    paddingY: 4,
    sleeperColor: '#F4F4F4',
    sleeperOpacity: 0.12,
    textShadow: '0 2px 0 rgba(0,0,0,0.14)',
    wordAnimation: 'pulse',
    maxWords: 1,
    lineOffsetY: -4,
  }),
  impact: createStyle({
    color: '#F5F5F5',
    strokeColor: '#111111',
    fontSize: 56,
    fontWeight: 900,
    strokeWidth: 6,
    highlightColor: '#F5F5F5',
    sleeperColor: '#D6D6D6',
    sleeperOpacity: 0.42,
    wordAnimation: 'bounce',
  }),
  notes: createStyle({
    color: '#111111',
    strokeWidth: 0,
    textTransform: 'uppercase',
    fontSize: 36,
    fontWeight: 900,
    highlightMode: 'box',
    boxScope: 'word',
    highlightColor: '#111111',
    highlightTextColor: '#111111',
    highlightBoxColor: '#F0DB45',
    paddingX: 5,
    paddingY: 2,
    sleeperColor: '#F3F3F3',
    sleeperOpacity: 0.86,
    wordAnimation: 'fade',
    wordDurationMs: 260,
    wordEasing: 'linear',
    idleAnimation: 'none',
    maxWords: 4,
    wordGap: 7,
    lineOffsetY: -4,
  }),
  vintage: createStyle({
    fontFamily: 'Georgia',
    color: '#E9D1AB',
    strokeColor: '#4E342E',
    strokeWidth: 2,
    textTransform: 'none',
    highlightColor: '#E9D1AB',
    sleeperColor: '#D0BEA2',
    sleeperOpacity: 0.64,
    wordAnimation: 'fade',
  }),
  mint: createStyle({
    color: '#FFFFFF',
    strokeColor: '#2E8B57',
    strokeWidth: 6,
    fontSize: 46,
    fontWeight: 900,
    highlightColor: '#DFFF47',
    sleeperColor: '#DFF2E3',
    sleeperOpacity: 0.58,
    textShadow: '0 3px 0 rgba(46,139,87,0.16)',
    wordAnimation: 'pulse',
  }),
  onepunch: createStyle({
    color: '#FFE900',
    strokeColor: '#111111',
    strokeWidth: 4,
    fontSize: 38,
    fontWeight: 900,
    letterSpacing: -0.03,
    highlightColor: '#FFE900',
    wordAnimation: 'bounce',
    wordDurationMs: 300,
    wordScaleFrom: 0.74,
    staggerMs: 0,
    maxWords: 1,
    sleeperColor: '#5E5E5E',
    sleeperOpacity: 0.12,
    textShadow: '0 2px 0 rgba(0,0,0,0.2)',
    lineOffsetY: -4,
  }),
  silka: createStyle({
    color: '#6F6C68',
    strokeWidth: 0,
    textTransform: 'none',
    italic: true,
    fontWeight: 600,
    highlightColor: '#507A33',
    sleeperColor: '#6F6C68',
    sleeperOpacity: 0.58,
    wordAnimation: 'fade',
  }),
  headlines: createStyle({
    fontFamily: 'Georgia',
    color: '#FFF3B0',
    strokeWidth: 0,
    textTransform: 'none',
    fontWeight: 700,
    highlightColor: '#FFF3B0',
    sleeperColor: '#DCCB84',
    sleeperOpacity: 0.58,
    textShadow: '2px 3px 0 rgba(0,0,0,0.85)',
    wordAnimation: 'slide',
  }),
  wasabi: createStyle({
    color: '#DDFB77',
    strokeColor: '#090909',
    strokeWidth: 1,
    textTransform: 'none',
    fontSize: 48,
    fontWeight: 700,
    italic: true,
    letterSpacing: -0.028,
    highlightColor: '#DDFB77',
    sleeperColor: '#AAB77B',
    sleeperOpacity: 0.54,
    textShadow: '4px 5px 0 rgba(0,0,0,0.9)',
    wordAnimation: 'slide',
    maxWords: 1,
    lineOffsetY: -2,
  }),
  emberduo: createStyle({
    color: '#FFB347',
    strokeColor: '#6A3200',
    strokeWidth: 2,
    highlightColor: '#FFD84C',
    wordAnimation: 'scale',
  }),
  pet: createStyle({
    color: '#FFFFFF',
    strokeColor: '#111111',
    strokeWidth: 2,
    textTransform: 'none',
    italic: true,
    highlightColor: '#FFEE3B',
    sleeperColor: '#F3F3F3',
    sleeperOpacity: 0.68,
    wordAnimation: 'drift',
  }),
  zen: createStyle({
    color: '#EAEAEA',
    strokeWidth: 0,
    textTransform: 'none',
    fontWeight: 500,
    lineHeight: 1.18,
    highlightColor: '#EAEAEA',
    sleeperColor: '#CFCFCF',
    sleeperOpacity: 0.58,
    wordAnimation: 'fade',
  }),
  mozi: createStyle({
    color: '#FFFFFF',
    strokeColor: '#111111',
    strokeWidth: 4,
    fontSize: 52,
    fontWeight: 900,
    highlightColor: '#1BFF36',
    sleeperColor: '#D6D6D6',
    sleeperOpacity: 0.48,
    wordAnimation: 'pop',
    maxWords: 2,
    lineOffsetY: -4,
  }),
  techtalk: createStyle({
    color: '#F2F2F2',
    strokeWidth: 0,
    textTransform: 'none',
    fontSize: 32,
    fontWeight: 500,
    letterSpacing: -0.012,
    lineHeight: 1.1,
    highlightColor: '#F2F2F2',
    sleeperColor: '#D8D8D8',
    sleeperOpacity: 0.62,
    textShadow: '0 1px 8px rgba(0,0,0,0.16)',
    wordAnimation: 'slide',
    wordDurationMs: 320,
    wordDistanceEm: 0.18,
    idleAnimation: 'none',
    maxWords: 4,
    wordGap: 9,
  }),
  yc: createStyle({
    color: '#FFFFFF',
    strokeColor: '#2E2E2E',
    strokeWidth: 0,
    textTransform: 'none',
    fontSize: 32,
    highlightMode: 'box',
    boxScope: 'line',
    highlightColor: '#FFFFFF',
    backgroundColor: '#3A3A3A',
    backgroundTextColor: '#FFFFFF',
    backgroundPaddingX: 12,
    backgroundPaddingY: 3,
    backgroundRadius: 2,
    sleeperColor: '#ECECEC',
    sleeperOpacity: 0.7,
    wordAnimation: 'fade',
  }),
  playfair: createStyle({
    fontFamily: 'Playfair Display',
    color: '#F6EFE6',
    strokeWidth: 0,
    textTransform: 'none',
    fontSize: 42,
    fontWeight: 700,
    italic: true,
    letterSpacing: -0.022,
    lineHeight: 1.06,
    highlightMode: 'fill',
    highlightColor: '#FFFFFF',
    sleeperColor: '#CDBEAE',
    sleeperOpacity: 0.7,
    textShadow: '0 3px 14px rgba(0,0,0,0.28)',
    wordAnimation: 'fade',
    wordDurationMs: 300,
    wordEasing: 'ease-out',
    idleAnimation: 'float',
    idleDurationMs: 2400,
    idleOffsetPx: 4,
    maxWords: 4,
    lineOffsetY: -4,
  }),
  popping: createStyle({
    color: '#FFFFFF',
    strokeWidth: 3,
    highlightMode: 'box',
    boxScope: 'word',
    highlightColor: '#FFFFFF',
    highlightTextColor: '#FFFFFF',
    highlightBoxColor: '#6A31E8',
    paddingX: 4,
    paddingY: 2,
    sleeperColor: '#F3F3F3',
    sleeperOpacity: 0.78,
    wordAnimation: 'pop',
  }),
  wiggle: createStyle({
    color: '#F2F2F2',
    strokeWidth: 0,
    textTransform: 'none',
    fontSize: 34,
    fontWeight: 500,
    italic: false,
    letterSpacing: -0.012,
    highlightColor: '#F2F2F2',
    sleeperColor: '#D7D7D7',
    sleeperOpacity: 0.62,
    textShadow: '0 1px 10px rgba(0,0,0,0.2)',
    wordAnimation: 'wiggle',
    wordDurationMs: 560,
    wordDistanceEm: 0.14,
    idleAnimation: 'drift-x',
    idleDurationMs: 1600,
    idleOffsetPx: 4,
    maxWords: 4,
    lineOffsetY: -2,
  }),
  typewriter: createStyle({
    fontFamily: 'JetBrains Mono',
    color: '#EFE6D7',
    strokeWidth: 0,
    textTransform: 'none',
    fontSize: 34,
    fontWeight: 600,
    letterSpacing: -0.008,
    lineHeight: 1.18,
    highlightMode: 'box',
    boxScope: 'word',
    highlightColor: '#FFF8EA',
    highlightTextColor: '#FFF8EA',
    highlightBoxColor: '#5A4631',
    paddingX: 4,
    paddingY: 1,
    backgroundColor: 'rgba(28, 20, 14, 0.9)',
    backgroundTextColor: '#EFE6D7',
    backgroundPaddingX: 18,
    backgroundPaddingY: 6,
    backgroundRadius: 6,
    backgroundShadow: '0 10px 20px rgba(0,0,0,0.2)',
    sleeperColor: '#CDBCA8',
    sleeperOpacity: 0.78,
    wordAnimation: 'fade',
    wordDurationMs: 180,
    wordEasing: 'steps(2, end)',
    idleAnimation: 'none',
    maxWords: 4,
    wordGap: 8,
    lineOffsetY: -4,
  }),
  wavy: createStyle({
    color: '#F7EC4E',
    strokeColor: '#111111',
    strokeWidth: 2,
    textTransform: 'none',
    fontSize: 38,
    fontWeight: 700,
    italic: false,
    letterSpacing: -0.018,
    lineHeight: 1.08,
    highlightColor: '#F7EC4E',
    sleeperColor: '#EAD548',
    sleeperOpacity: 0.7,
    textShadow: '0 3px 0 rgba(0,0,0,0.7)',
    wordAnimation: 'wiggle',
    wordDurationMs: 540,
    wordDistanceEm: 0.18,
    idleAnimation: 'drift-x',
    idleDurationMs: 1800,
    idleOffsetPx: 7,
    maxWords: 3,
  }),
  kinetictypography: createStyle({
    color: '#E8E8E8',
    strokeColor: '#121212',
    strokeWidth: 0,
    textTransform: 'uppercase',
    fontSize: 44,
    fontWeight: 900,
    letterSpacing: -0.06,
    lineHeight: 0.94,
    highlightMode: 'fill',
    highlightColor: '#E8E8E8',
    sleeperColor: '#7B7B7B',
    sleeperOpacity: 0.24,
    textShadow: '0 3px 12px rgba(0,0,0,0.22)',
    wordAnimation: 'slide',
    wordDurationMs: 260,
    wordDistanceEm: 1.02,
    wordScaleFrom: 0.92,
    staggerMs: 0,
    idleAnimation: 'none',
    maxWords: 1,
    wordGap: 6,
    lineOffsetY: -2,
  }),
  ticker: createStyle({
    color: '#D8D8D8',
    strokeWidth: 0,
    fontSize: 28,
    highlightColor: '#FFD541',
    sleeperColor: '#AFAFAF',
    sleeperOpacity: 0.68,
    wordAnimation: 'slide',
    wordDurationMs: 300,
    wordDistanceEm: 0.22,
    maxWords: 4,
    lineOffsetY: -2,
  }),
  squiggly: createStyle({
    color: '#F3F3F3',
    strokeWidth: 0,
    textTransform: 'none',
    highlightMode: 'box',
    boxScope: 'line',
    highlightColor: '#F3F3F3',
    backgroundColor: '#151515',
    backgroundTextColor: '#F3F3F3',
    backgroundPaddingX: 14,
    backgroundPaddingY: 4,
    backgroundRadius: 4,
    sleeperColor: '#D8D8D8',
    sleeperOpacity: 0.74,
    wordAnimation: 'wiggle',
  }),
  glitchv2: createStyle({
    color: '#F7F7F7',
    strokeColor: '#FF2FCF',
    strokeWidth: 2,
    highlightColor: '#F7F7F7',
    sleeperColor: '#EAEAEA',
    sleeperOpacity: 0.66,
    textShadow: '2px 0 0 rgba(255,47,207,0.35), -2px 0 0 rgba(66,232,255,0.35)',
    wordAnimation: 'glitch',
  }),
  glitch: createStyle({
    color: '#F8F8F8',
    strokeColor: '#FF00F7',
    strokeWidth: 2,
    highlightColor: '#F8F8F8',
    sleeperColor: '#E6E6E6',
    sleeperOpacity: 0.66,
    textShadow: '2px 0 0 rgba(255,0,247,0.28), -2px 0 0 rgba(0,255,156,0.28)',
    wordAnimation: 'glitch',
  }),
  striker: createStyle({
    color: '#FFFFFF',
    strokeColor: '#111111',
    strokeWidth: 3,
    textTransform: 'uppercase',
    fontSize: 45,
    fontWeight: 900,
    highlightMode: 'box',
    boxScope: 'line',
    highlightColor: '#FF62DF',
    backgroundColor: '#111111',
    backgroundTextColor: '#FFFFFF',
    backgroundPaddingX: 16,
    backgroundPaddingY: 4,
    backgroundRadius: 8,
    sleeperColor: '#D7D7D7',
    sleeperOpacity: 0.54,
    wordAnimation: 'scale',
    maxWords: 4,
    lineOffsetY: -5,
  }),
  trophy: createStyle({
    color: '#F2F2F2',
    strokeWidth: 0,
    highlightColor: '#F2F2F2',
    sleeperColor: '#D7D7D7',
    sleeperOpacity: 0.52,
    wordAnimation: 'pop',
  }),
  prism: createStyle({
    color: '#FFFFFF',
    strokeWidth: 0,
    highlightMode: 'box',
    boxScope: 'word',
    highlightColor: '#FFFFFF',
    highlightTextColor: '#FFFFFF',
    highlightBoxColor: '#A7D94E',
    paddingX: 5,
    paddingY: 2,
    sleeperColor: '#F1F1F1',
    sleeperOpacity: 0.74,
    wordAnimation: 'pulse',
  }),
  halo: createStyle({
    fontFamily: 'Georgia',
    color: '#FFD61F',
    strokeWidth: 0,
    textTransform: 'none',
    fontSize: 50,
    fontWeight: 700,
    highlightColor: '#FFD61F',
    highlightMode: 'glow',
    sleeperColor: '#E1C35F',
    sleeperOpacity: 0.46,
    textShadow: '0 0 18px rgba(255,214,31,0.62)',
    wordAnimation: 'pulse',
    lineOffsetY: -2,
  }),
  crimson: createStyle({
    color: '#FF9C31',
    strokeColor: '#5C2600',
    strokeWidth: 1,
    highlightColor: '#FF9C31',
    sleeperColor: '#D68A43',
    sleeperOpacity: 0.58,
    wordAnimation: 'scale',
  }),
  candy: createStyle({
    color: '#FFFFFF',
    strokeWidth: 0,
    textTransform: 'none',
    fontSize: 44,
    fontWeight: 700,
    italic: true,
    letterSpacing: -0.02,
    lineHeight: 1.06,
    highlightMode: 'underline',
    boxScope: 'line',
    highlightColor: '#FFFFFF',
    highlightUnderlineColor: '#FFFFFF',
    highlightUnderlineHeight: 4,
    backgroundColor: '#E2557E',
    backgroundTextColor: '#FFFFFF',
    backgroundPaddingX: 20,
    backgroundPaddingY: 8,
    backgroundRadius: 18,
    backgroundShadow: '0 12px 24px rgba(0,0,0,0.22)',
    sleeperColor: '#FFD3DE',
    sleeperOpacity: 0.72,
    wordAnimation: 'bounce',
    wordDurationMs: 420,
    maxWords: 4,
    lineOffsetY: -4,
  }),
  thinkmedia: createStyle({
    color: '#FFFFFF',
    strokeColor: '#111111',
    strokeWidth: 2,
    textTransform: 'uppercase',
    fontSize: 48,
    fontWeight: 900,
    italic: true,
    letterSpacing: -0.035,
    highlightColor: '#2CFF3C',
    sleeperColor: '#D8D8D8',
    sleeperOpacity: 0.58,
    textShadow: '3px 4px 0 rgba(0,0,0,0.82)',
    wordAnimation: 'slide',
    maxWords: 4,
  }),
  march: createStyle({
    color: '#FF4ED8',
    strokeColor: '#0F0F0F',
    strokeWidth: 2,
    highlightColor: '#FF4ED8',
    sleeperColor: '#F0D4EA',
    sleeperOpacity: 0.62,
    fontSize: 48,
    fontWeight: 900,
    wordAnimation: 'bounce',
  }),
  pulse: createStyle({
    color: '#FFFFFF',
    strokeColor: '#101010',
    strokeWidth: 2,
    highlightColor: '#34D399',
    sleeperColor: '#EDEDED',
    sleeperOpacity: 0.62,
    wordAnimation: 'pulse',
  }),
  slidedown: createStyle({
    color: '#FFFFFF',
    strokeWidth: 0,
    textTransform: 'none',
    fontSize: 40,
    fontWeight: 600,
    italic: true,
    highlightColor: '#4BE8FF',
    sleeperColor: '#E8E8E8',
    sleeperOpacity: 0.66,
    wordAnimation: 'slide',
    wordDurationMs: 300,
    wordDistanceEm: 0.32,
    wordEasing: 'cubic-bezier(0.16, 1, 0.3, 1)',
    idleAnimation: 'none',
    maxWords: 4,
    lineOffsetY: -3,
  }),
  punch: createStyle({
    color: '#FFFFFF',
    strokeColor: '#111111',
    strokeWidth: 1,
    textTransform: 'none',
    fontFamily: 'Georgia',
    fontSize: 32,
    fontWeight: 600,
    highlightColor: '#E8DE5A',
    sleeperColor: '#E7E7E7',
    sleeperOpacity: 0.7,
    wordAnimation: 'pop',
  }),
  lemon: createStyle({
    color: '#FFFFFF',
    strokeWidth: 0,
    textTransform: 'none',
    fontSize: 42,
    fontWeight: 700,
    italic: true,
    highlightColor: '#F5EF57',
    backgroundColor: '#1F1F22',
    backgroundTextColor: '#FFFFFF',
    backgroundPaddingX: 16,
    backgroundPaddingY: 6,
    backgroundRadius: 8,
    sleeperColor: '#D6D6D6',
    sleeperOpacity: 0.52,
    textShadow: '0 1px 0 rgba(0,0,0,0.18)',
    wordAnimation: 'bounce',
    maxWords: 4,
    lineOffsetY: -3,
  }),
  surge: createStyle({
    color: '#FFFFFF',
    strokeColor: '#A432FF',
    strokeWidth: 1,
    highlightColor: '#B14DFF',
    sleeperColor: '#F1D9FF',
    sleeperOpacity: 0.7,
    textShadow: '0 0 12px rgba(177,77,255,0.32)',
    wordAnimation: 'scale',
  }),
  bubblegum: createStyle({
    color: '#FFD5E4',
    strokeWidth: 0,
    textTransform: 'none',
    highlightMode: 'box',
    boxScope: 'word',
    highlightColor: '#FFFFFF',
    highlightTextColor: '#FFFFFF',
    highlightBoxColor: '#6EBEFF',
    paddingX: 4,
    paddingY: 2,
    sleeperColor: '#F0D8E4',
    sleeperOpacity: 0.8,
    wordAnimation: 'pop',
  }),
  cloudfall: createStyle({
    color: '#F1F1F1',
    strokeColor: '#283341',
    strokeWidth: 0,
    textTransform: 'none',
    highlightColor: '#F1F1F1',
    sleeperColor: '#D9D9D9',
    sleeperOpacity: 0.58,
    wordAnimation: 'blur-in',
  }),
  tremor: createStyle({
    color: '#E7E2D6',
    strokeWidth: 0,
    textTransform: 'none',
    fontSize: 36,
    fontWeight: 500,
    letterSpacing: -0.016,
    highlightMode: 'fill',
    highlightColor: '#D9C84B',
    sleeperColor: '#B3AEA4',
    sleeperOpacity: 0.56,
    textShadow: '0 1px 10px rgba(0,0,0,0.18)',
    wordAnimation: 'wiggle',
    wordDurationMs: 460,
    wordDistanceEm: 0.12,
    idleAnimation: 'drift-x',
    idleDurationMs: 1400,
    idleOffsetPx: 3,
    maxWords: 4,
    lineOffsetY: -2,
  }),
  stretchy: createStyle({
    color: '#F8F7FF',
    strokeColor: '#1B1638',
    strokeWidth: 3,
    textTransform: 'uppercase',
    fontSize: 50,
    fontWeight: 900,
    letterSpacing: -0.045,
    lineHeight: 0.98,
    highlightMode: 'box',
    boxScope: 'word',
    highlightColor: '#FFFFFF',
    highlightTextColor: '#FFFFFF',
    highlightBoxColor: '#5B4BFF',
    paddingX: 7,
    paddingY: 2,
    sleeperColor: '#B9B4E8',
    sleeperOpacity: 0.5,
    textShadow: '0 4px 16px rgba(28,22,55,0.22)',
    wordAnimation: 'stretch',
    wordDurationMs: 420,
    wordScaleFrom: 0.7,
    idleAnimation: 'pulse',
    idleDurationMs: 1600,
    idleOffsetPx: 3,
    maxWords: 2,
    wordGap: 8,
  }),
  bloom: createStyle({
    fontFamily: 'Georgia',
    color: '#F9F4F4',
    strokeColor: '#1A1A1A',
    strokeWidth: 2,
    textTransform: 'none',
    fontSize: 44,
    fontWeight: 700,
    highlightMode: 'glow',
    highlightColor: '#AE4B9D',
    sleeperColor: '#E6DBE5',
    sleeperOpacity: 0.6,
    textShadow: '0 0 14px rgba(174,75,157,0.22)',
    wordAnimation: 'blur-in',
    maxWords: 4,
  }),
  hype: createStyle({
    color: '#FFFFFF',
    strokeColor: '#111111',
    strokeWidth: 3,
    highlightColor: '#32FF5A',
    sleeperColor: '#ECECEC',
    sleeperOpacity: 0.62,
    fontSize: 50,
    fontWeight: 900,
    wordAnimation: 'bounce',
  }),
  blur: createStyle({
    color: '#F1F1F1',
    strokeWidth: 0,
    highlightColor: '#F1F1F1',
    sleeperColor: '#CFCFCF',
    sleeperOpacity: 0.38,
    textShadow: '0 0 16px rgba(255,255,255,0.35)',
    wordAnimation: 'blur-in',
  }),
}

export const DEFAULT_CAPTION_TRANSCRIPT: CaptionCueWord[] = [
  { word: 'think', startMs: 14, endMs: 936 },
  { word: 'of', startMs: 996, endMs: 1036 },
  { word: 'this', startMs: 1076, endMs: 1277 },
  { word: 'little', startMs: 1357, endMs: 1617 },
  { word: 'podcast', startMs: 1697, endMs: 2178 },
  { word: 'conversation', startMs: 2218, endMs: 2860 },
  { word: 'as', startMs: 2900, endMs: 3601 },
  { word: 'the', startMs: 3641, endMs: 3761 },
  { word: 'appetizer', startMs: 3882, endMs: 4503 },
  { word: 'to', startMs: 4523, endMs: 5064 },
  { word: 'the', startMs: 5084, endMs: 5164 },
  { word: 'final', startMs: 5244, endMs: 5545 },
  { word: 'meal', startMs: 5625, endMs: 5906 },
  { word: 'before', startMs: 6306, endMs: 6727 },
  { word: 'the', startMs: 6767, endMs: 7128 },
  { word: 'apocalypse', startMs: 7188, endMs: 8210 },
]

export function buildPreviewCaptionCues(maxWords: number): CaptionCue[] {
  const size = Math.max(1, maxWords)
  const cues: CaptionCue[] = []
  for (let index = 0; index < DEFAULT_CAPTION_TRANSCRIPT.length; index += size) {
    const words = DEFAULT_CAPTION_TRANSCRIPT.slice(index, index + size)
    cues.push({
      text: words.map((item) => item.word).join(' '),
      startMs: words[0]!.startMs,
      endMs: words[words.length - 1]!.endMs,
      words,
    })
  }
  return cues
}

export function getCaptionStyleConfig(key: string): CaptionStyleConfig {
  return captionStyleConfigMap[key] ?? WHITE
}

export function buildCaptionRenderConfig(
  style: CaptionStyleConfig,
  overrides?: Record<string, unknown>,
): CaptionRenderConfig {
  const font = style.font.css
  const highlight = style.highlightFont
  const background = style.background
  const animation = style.animation
  const backgroundPadding = parsePadding(background?.css.padding, 16, 6)
  const highlightPadding = parsePadding(highlight?.css.padding, 10, 6)
  const derivedHighlightTextColor =
    (overrides?.highlightTextColor as string)
    ?? highlight?.css.color
    ?? (highlight?.mode === 'box' ? font.color : undefined)
  const derivedBackgroundTextColor =
    (overrides?.backgroundTextColor as string)
    ?? background?.css.color
    ?? (background?.css.backgroundColor ? font.color : undefined)

  return {
    fontFamily: (overrides?.fontFamily as string) ?? font.fontFamily ?? 'Inter',
    fontSize: (overrides?.fontSize as number) ?? font.fontSize ?? 46,
    color: (overrides?.color as string) ?? font.color ?? '#FFFFFF',
    fontWeight: (overrides?.fontWeight as number) ?? font.fontWeight ?? 800,
    strokeColor: (overrides?.strokeColor as string) ?? font.WebkitTextStrokeColor ?? '#000000',
    strokeWidth: (overrides?.strokeWidth as number) ?? font.WebkitTextStrokeWidth ?? 0,
    textTransform: (overrides?.textTransform as CaptionRenderConfig['textTransform']) ?? font.textTransform ?? 'uppercase',
    letterSpacing: (overrides?.letterSpacing as number) ?? font.letterSpacing ?? -0.03,
    lineHeight: (overrides?.lineHeight as number) ?? font.lineHeight ?? 1.08,
    textAlign: (overrides?.textAlign as CaptionRenderConfig['textAlign']) ?? font.textAlign ?? 'center',
    textShadow: (overrides?.textShadow as string) ?? font.textShadow ?? '0 2px 10px rgba(0,0,0,0.45)',
    wordGap: (overrides?.wordGap as number) ?? style.wordGap ?? 10,
    maxWordsPerLine: (overrides?.maxWordsPerLine as number) ?? (overrides?.maxWords as number) ?? style.maxWords ?? 3,
    staggerMs: (overrides?.staggerMs as number) ?? animation?.staggerMs ?? 90,
    italic: (overrides?.italic as boolean) ?? font.fontStyle === 'italic',
    wordAnimation: (overrides?.wordAnimation as CaptionAnimation) ?? (overrides?.animation as CaptionAnimation) ?? animation?.word ?? 'pop',
    wordDurationMs: (overrides?.wordDurationMs as number) ?? animation?.wordDurationMs ?? 480,
    wordEasing: (overrides?.wordEasing as string) ?? animation?.wordEasing ?? 'cubic-bezier(0.22, 1, 0.36, 1)',
    wordDistanceEm: (overrides?.wordDistanceEm as number) ?? animation?.wordDistanceEm ?? 0.62,
    wordScaleFrom: (overrides?.wordScaleFrom as number) ?? animation?.wordScaleFrom ?? 0.42,
    idleAnimation: (overrides?.idleAnimation as CaptionIdleAnimation) ?? animation?.idle ?? 'none',
    idleDurationMs: (overrides?.idleDurationMs as number) ?? animation?.idleDurationMs ?? 2200,
    idleOffsetPx: (overrides?.idleOffsetPx as number) ?? animation?.idleOffsetPx ?? 8,
    highlightMode: (overrides?.highlightMode as CaptionHighlightMode) ?? highlight?.mode ?? 'fill',
    boxScope: (overrides?.boxScope as CaptionBoxScope) ?? highlight?.scope ?? 'word',
    highlightLineIndex: (overrides?.highlightLineIndex as number) ?? highlight?.lineIndex ?? 0,
    highlightColor: (overrides?.highlightColor as string) ?? highlight?.css.color,
    highlightTextColor: derivedHighlightTextColor,
    highlightBoxColor: (overrides?.highlightBoxColor as string) ?? highlight?.css.backgroundColor,
    highlightUnderlineColor: (overrides?.highlightUnderlineColor as string) ?? highlight?.css.underlineColor,
    highlightUnderlineHeight: (overrides?.highlightUnderlineHeight as number) ?? highlight?.css.underlineHeight ?? 6,
    borderRadius: (overrides?.borderRadius as number) ?? highlight?.css.borderRadius ?? 12,
    paddingX: (overrides?.paddingX as number) ?? highlightPadding.x,
    paddingY: (overrides?.paddingY as number) ?? highlightPadding.y,
    sleeperColor: (overrides?.sleeperColor as string) ?? style.sleeperFont?.css.color,
    sleeperOpacity: (overrides?.sleeperOpacity as number) ?? style.sleeperFont?.css.opacity,
    backgroundColor: (overrides?.backgroundColor as string) ?? background?.css.backgroundColor,
    backgroundScope: (overrides?.backgroundScope as CaptionBackgroundScope) ?? background?.scope,
    backgroundTextColor: derivedBackgroundTextColor,
    backgroundRadius: (overrides?.backgroundRadius as number) ?? background?.css.borderRadius ?? 12,
    backgroundPaddingX: (overrides?.backgroundPaddingX as number) ?? backgroundPadding.x,
    backgroundPaddingY: (overrides?.backgroundPaddingY as number) ?? backgroundPadding.y,
    backgroundShadow: (overrides?.backgroundShadow as string) ?? background?.css.boxShadow,
    lineOffsetY: (overrides?.lineOffsetY as number) ?? style.lineOffsetY ?? 0,
  }
}
